/** Field, Input, Textarea: a labelled form control in the search field's own skin. */
import type { ComponentProps, ReactNode } from "react";
import { cx } from "./cx";

const CONTROL =
  "w-full rounded-lg border-hair border-border bg-card px-3 text-body text-foreground outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-muted-foreground hover:border-border-hover focus:border-ring focus:shadow-field-focus disabled:pointer-events-none disabled:opacity-50 aria-[invalid=true]:border-destructive";

export function Input({ className, ...props }: ComponentProps<"input">) {
  return <input data-slot="input" className={cx(CONTROL, "h-9 leading-none", className)} {...props} />;
}

export function Textarea({ className, rows = 3, ...props }: ComponentProps<"textarea">) {
  return <textarea data-slot="textarea" rows={rows} className={cx(CONTROL, "resize-none py-2", className)} {...props} />;
}

interface FieldProps {
  label: string;
  /** Hide the label visually when the placeholder already says it. */
  hideLabel?: boolean;
  hint?: string;
  /** Replaces the hint and is announced. */
  error?: string;
  /** One Input or Textarea. */
  children: ReactNode;
  className?: string;
}

export function Field({ label, hideLabel = false, hint, error, children, className }: FieldProps) {
  return (
    <label className={cx("flex w-full flex-col gap-1.5", className)}>
      <span className={cx("text-meta font-medium text-secondary-foreground", hideLabel && "sr-only")}>{label}</span>
      {children}
      {error ? (
        <span role="alert" className="text-meta text-destructive">
          {error}
        </span>
      ) : (
        hint && <span className="text-meta text-muted-foreground">{hint}</span>
      )}
    </label>
  );
}
